import { IntlShape } from "react-intl";
import _pick from "lodash/fp/pick";
import _mapValues from "lodash/mapValues";
import { Options } from "src/components/atoms/inputs/FlowioSelect/types";
import {
  Dictionary,
  ReactSelectSearchItem,
  ReactSelectSearchItem as RSSI,
  SelectItem,
} from "../globalTypes";
import { IntlMessageId } from "../assets/localization/types";

const { isArray } = Array;

type Value = string | number;
type Label = string | number;

export const createOption = <V extends Value, L extends Label = Label>(
  value: V,
  label: L
) => {
  const labelString = String(label) as `${L}`;
  const valueString = String(value) as `${V}`;

  return {
    label: labelString,
    value: valueString,
    text: labelString,
    name: labelString,
  };
};

export const createSelectOption = <V extends Value, L extends Label = Label>(
  value: V,
  label: L
) => {
  const labelString = String(label) as `${L}`;
  const valueString = String(value) as `${V}`;

  return {
    value: valueString,
    name: labelString,
  };
};

export const createUniformOption = <V extends Value>(allProps: V) =>
  createOption<V, V>(allProps, allProps);

export const createOptions = <V extends Value, L extends Label = Label>(
  entries: [V, L][]
) => entries.map((entry) => createOption<V, L>(...entry));

export const createSelectOptions = <V extends Value, L extends Label = Label>(
  entries: [V, L][]
) => entries.map((entry) => createSelectOption<V, L>(...entry));

export const createUniformOptions = <V extends Value>(entries: V[]) =>
  entries.map(createUniformOption);

export const createLocalizedOption = <
  V extends Value,
  ID extends IntlMessageId = IntlMessageId
>(
  intl: IntlShape,
  entry: [V, ID]
) => {
  const [value, id] = entry;
  return createOption<V, string>(value, intl.formatMessage({ id }));
};

export const createLocalizedOptions = <
  V extends Value,
  IDs extends IntlMessageId = IntlMessageId
>(
  intl: IntlShape,
  entries: [V, IDs][]
) => {
  const translatedEntries: [V, Label][] = entries.map(([value, intlId]) => [
    value,
    intl.formatMessage({ id: intlId }),
  ]);

  return createOptions(translatedEntries);
};

type EnumLikeRecord = Record<string | number, string | number>;

export type EnumOptionDict<E extends EnumLikeRecord> = {
  [k in E[keyof E]]: RSSI[];
};

/**
 * Dostane enum, vrátí objekt co má jako klíče hodnoty enumu a
 * všechny hodnoty jsou option list
 *
 */
export const createOptionDictFromEnum = <Enum extends EnumLikeRecord>(
  dict: Enum,
  defaultOptions: ReactSelectSearchItem[] = []
) => {
  const enumValues = Object.values(dict);
  const entries = enumValues.map((val) => [val, defaultOptions]);
  return Object.fromEntries(entries) as EnumOptionDict<Enum>;
};

/**
 * Dostane jako parametr enum a vrati pole optionu, kde jsou labely stejne jako values
 * @param dict
 * @returns Options
 */

export const createOptionsFromEnum = <Enum extends EnumLikeRecord>(
  dict: Enum
): Options => {
  const enumValues = Object.values(dict);
  return enumValues.map((val) => createSelectOption(val, val));
};

export function isSelectItemRange(item: SelectItem) {
  const { rangeValue } = item;
  if (!isArray(rangeValue)) return false;

  const [from, to] = rangeValue;
  const isFromBlank = from === null || from === undefined || from === "";
  const isToBlank = to === null || to === undefined || to === "";
  if (isFromBlank && isToBlank) return false;

  return true;
}

/**
 * Blueprint = slovník všech potenciálních optionů
 * Pro blueprint vrátí funkci, které dám admissible klíče y blueprintu a
 * ona mi pro ně udělá pole optionů - používá se, ve složitějších formulářích,
 * kde ve stejném selectu měním available options podle obsahu jiného pole
 * (touto funkcí si předgeneruji slovník option listů)
 */
export const makeOptionCreatorForBlueprint =
  <K extends string | number, V extends string = IntlMessageId>(
    blueprint: Dictionary<K, V>
  ) =>
  (pickedKeys?: K[] | readonly K[]) => {
    type Entries = [`${K}`, V][];

    if (!pickedKeys) {
      const entries = Object.entries(blueprint) as Entries;
      return createOptions(entries);
    }
    const entries = Object.entries(_pick(pickedKeys, blueprint)) as Entries;
    return createOptions(entries);
  };

export const localizeOptionBlueprint = <
  K extends string | number,
  V extends IntlMessageId
>(
  intl: IntlShape,
  blueprint: Record<K, V>
) => {
  // Nevim k čemu je třeba ten cast, ale TS to bez něj odvodí blbě
  const result = _mapValues(blueprint, (id) => intl.formatMessage({ id }));
  return result as unknown as Record<K, string>;
};
